import { memo, useMemo, type ReactNode } from "react"
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar as RechartsRadar,
  RadarChart,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { DollarSign, Gavel, Megaphone, Calendar, Settings } from "lucide-react"
import { useDealPageData } from "@/hooks/deals/useDealPageData"
import type { KeyTermId } from "@/types/demo.types"

interface KeyTermChartProps {
  termId: KeyTermId
}

interface AdvanceProgressProps {
  progressPercent: number
  totalAdvance: number
  installmentCount: number
}

interface OwnershipSplitProps {
  segments: Array<{ name: string; value: number; color: string }>
}

interface RiskHorizonProps {
  points: Array<{ label: string; years: number; color: string }>
}

interface HolisticAnalysisProps {
  axes: Array<{ subject: string; score: number; fullMark: number }>
}

type TooltipValue = number | string

interface TooltipPayloadItem {
  name?: string | number
  value?: TooltipValue
  color?: string
  fill?: string
}

interface StyledTooltipProps {
  active?: boolean
  label?: string | number
  payload?: TooltipPayloadItem[]
  valueFormatter?: (value: TooltipValue) => string
}

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
})

const ChartFrame = memo(function ChartFrame({ children }: { children: ReactNode }) {
  return (
    <div className="h-[200px] rounded-lg border border-border/50 bg-accent/40 p-2">
      {children}
    </div>
  )
})

const ChartFallback = memo(function ChartFallback() {
  return (
    <div className="h-full min-h-[150px] flex items-center justify-center rounded-md bg-muted/30">
      <p className="text-xs text-muted-foreground">Chart unavailable</p>
    </div>
  )
})

const StyledChartTooltip = memo(function StyledChartTooltip({
  active,
  label,
  payload,
  valueFormatter,
}: StyledTooltipProps) {
  if (!active || !payload || payload.length === 0) {
    return null
  }

  return (
    <div className="min-w-[136px] rounded-xl border border-border/70 bg-background/95 px-3 py-2 shadow-lg backdrop-blur-sm">
      {label ? (
        <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
      ) : null}
      <div className="space-y-1">
        {payload.map((item, index) => {
          const dotColor = item.color ?? item.fill ?? "var(--color-primary)"
          const valueText = valueFormatter
            ? valueFormatter(item.value ?? "-")
            : String(item.value ?? "-")

          return (
            <div key={`${String(item.name ?? "value")}-${index}`} className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <span className="size-2 rounded-full" style={{ backgroundColor: dotColor }} aria-hidden="true" />
                <span className="text-[11px] text-muted-foreground">{item.name ?? "Value"}</span>
              </div>
              <span className="text-[11px] font-semibold text-foreground">{valueText}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
})

const AdvanceProgressVisualization = memo(function AdvanceProgressVisualization({
  progressPercent,
  totalAdvance,
  installmentCount,
}: AdvanceProgressProps) {
  const radialData = useMemo(
    () => [{ name: "Received", value: progressPercent, fill: "var(--color-primary)" }],
    [progressPercent],
  )

  return (
    <div className="h-full flex flex-col">
      <div className="relative flex-1 min-h-[150px]">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            data={radialData}
            startAngle={90}
            endAngle={-270}
            innerRadius="78%"
            outerRadius="100%"
          >
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar dataKey="value" background={{ fill: "var(--color-primary)", opacity: 0.15 }} cornerRadius={10} />
            <Tooltip
              cursor={false}
              content={(props: StyledTooltipProps) => (
                <StyledChartTooltip
                  {...props}
                  valueFormatter={(value) => `${value}%`}
                />
              )}
            />
          </RadialBarChart>
        </ResponsiveContainer>

        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-semibold text-foreground">{progressPercent}%</span>
          <span className="text-[10px] text-muted-foreground">
            {currencyFormatter.format(totalAdvance)} total
          </span>
        </div>
      </div>

      <p className="size-fit rounded-full bg-foreground text-background px-2.5 py-1 text-[10px] text-center">
        {installmentCount} installments
      </p>
    </div>
  )
})

const OwnershipSplitVisualization = memo(function OwnershipSplitVisualization({
  segments,
}: OwnershipSplitProps) {
  const totalPercentage = useMemo(
    () => segments.reduce((sum, segment) => sum + segment.value, 0),
    [segments],
  )

  return (
    <div className="h-full flex flex-col">
      <div className="relative flex-1 min-h-[140px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={segments}
              dataKey="value"
              nameKey="name"
              innerRadius="56%"
              outerRadius="82%"
              paddingAngle={2}
              stroke="transparent"
            >
              {segments.map((segment) => (
                <Cell key={segment.name} fill={segment.color} />
              ))}
            </Pie>
            <Tooltip
              cursor={false}
              content={(props: StyledTooltipProps) => (
                <StyledChartTooltip
                  {...props}
                  valueFormatter={(value) => `${value}%`}
                />
              )}
            />
          </PieChart>
        </ResponsiveContainer>

        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-semibold text-foreground">{totalPercentage}%</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1.5 mt-1">
        {segments.map((segment) => (
          <div key={segment.name} className="flex items-center gap-1.5">
            <span
              className="size-2 rounded-full"
              style={{ backgroundColor: segment.color }}
              aria-hidden="true"
            />
            <span className="text-[10px] text-muted-foreground">
              {segment.name} {segment.value}%
            </span>
          </div>
        ))}
      </div>
    </div>
  )
})

const RiskHorizonVisualization = memo(function RiskHorizonVisualization({
  points,
}: RiskHorizonProps) {
  const maxYears = useMemo(
    () => Math.max(...points.map((point) => point.years), 0) + 1,
    [points],
  )

  return (
    <div className="h-full min-h-[160px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={points}
          margin={{ top: 6, right: 8, left: 2, bottom: 0 }}
        >
          <XAxis type="number" hide domain={[0, maxYears]} />
          <YAxis
            type="category"
            dataKey="label"
            axisLine={false}
            tickLine={false}
            alignmentBaseline="text-before-edge"
            width={96}
            tick={{ fontSize: 10, fill: "#71717a" }}
          />
          <Tooltip
            cursor={false}
            content={(props: StyledTooltipProps) => (
              <StyledChartTooltip
                {...props}
                valueFormatter={(value) => `${value} years`}
              />
            )}
          />
          <Bar dataKey="years" radius={[4, 4, 4, 4]} barSize={12}>
            {points.map((point) => (
              <Cell key={point.label} fill={point.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
})

const AXIS_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Financial: DollarSign,
  Legal: Gavel,
  Marketing: Megaphone,
  Scheduling: Calendar,
  Operations: Settings,
}

interface AxisTickProps {
  x: number
  y: number
  payload: { value: string }
  cx: number
  cy: number
}

function HolisticAxisTick({ x, y, payload, cx, cy }: AxisTickProps) {
  const Icon = AXIS_ICONS[payload.value]
  const dx = x - cx
  const dy = y - cy
  const len = Math.sqrt(dx * dx + dy * dy) || 1
  const offsetX = x + (dx / len) * 10
  const offsetY = y + (dy / len) * 10

  return (
    <g transform={`translate(${offsetX},${offsetY})`}>
      {Icon ? (
        <foreignObject x={-8} y={-8} width={16} height={16} style={{ overflow: "visible" }}>
          <Icon className="size-4 text-muted-foreground" />
        </foreignObject>
      ) : (
        <text
          x={0}
          y={0}
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-muted-foreground text-[10px]"
        >
          {payload.value}
        </text>
      )}
    </g>
  )
}

const HolisticAnalysisVisualization = memo(function HolisticAnalysisVisualization({
  axes,
}: HolisticAnalysisProps) {
  return (
    <div className="h-full flex flex-col">
      <div className="relative flex-1 min-h-[150px]">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={axes} cx="50%" cy="50%" outerRadius="65%">
            <PolarGrid stroke="hsl(var(--border))" />
            <PolarAngleAxis
              dataKey="subject"
              tick={HolisticAxisTick}
            />
            <PolarRadiusAxis
              domain={[0, 5]}
              tick={false}
              axisLine={false}
            />
            <RechartsRadar
              name="Score"
              dataKey="score"
              stroke="#6d7dff"
              fill="#6d7dff"
              fillOpacity={0.25}
              strokeWidth={2}
            />
            <Tooltip
              cursor={false}
              content={(props: StyledTooltipProps) => (
                <StyledChartTooltip
                  {...props}
                  valueFormatter={(value) => `${value}/5`}
                />
              )}
            />
          </RadarChart>
        </ResponsiveContainer>

      </div>
    </div>
  )
})

export const KeyTermChart = memo(function KeyTermChart({ termId }: KeyTermChartProps) {
  const { keyTermChartData } = useDealPageData()
  const chartData = useMemo(() => keyTermChartData[termId], [keyTermChartData, termId])

  if (!chartData) {
    return (
      <ChartFrame>
        <ChartFallback />
      </ChartFrame>
    )
  }

  switch (chartData.kind) {
    case "advance_progress":
      return (
        <ChartFrame>
          <AdvanceProgressVisualization
            progressPercent={chartData.progressPercent}
            totalAdvance={chartData.totalAdvance}
            installmentCount={chartData.installmentCount}
          />
        </ChartFrame>
      )
    case "ownership_split":
      return (
        <ChartFrame>
          <OwnershipSplitVisualization segments={chartData.segments} />
        </ChartFrame>
      )
    case "risk_horizon":
      return (
        <ChartFrame>
          <RiskHorizonVisualization points={chartData.points} />
        </ChartFrame>
      )
    case "holistic_analysis":
      return (
        <ChartFrame>
          <HolisticAnalysisVisualization axes={chartData.axes} />
        </ChartFrame>
      )
    default:
      return (
        <ChartFrame>
          <ChartFallback />
        </ChartFrame>
      )
  }
})
